import React from 'react';
import { Text } from 'src/modules/shared/components/atoms/text';
import { CopyableValue } from 'src/modules/shared/components/molecules/copyable-value/copyable-value';
import { DescriptionList } from 'src/modules/shared/components/molecules/description-list/description-list';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

/** Props for a DNS record the customer has to create. */
interface WebsiteDnsRecordProps {
  /** What the record is for. */
  title: string;
  /** Record type, such as CNAME. */
  type: string;
  /** Record name. */
  name: string;
  /** Record value. */
  value: string;
}

/**
 * One DNS record laid out the way DNS panels ask for it: type, name and
 * value, with the name and value ready to copy.
 *
 * @param props - Record purpose, type, name and value.
 * @returns The record card.
 */
export const WebsiteDnsRecord: React.FC<WebsiteDnsRecordProps> = ({
  title,
  type,
  name,
  value,
}) => {
  const { t } = useI18n();
  const nameLabel = t('websites.settings.domains.records.name');
  const valueLabel = t('websites.settings.domains.records.value');

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-3">
      <Text size="small" weight="medium">
        {title}
      </Text>
      <DescriptionList
        className="mt-2 sm:gap-y-1"
        items={[
          {
            label: t('websites.settings.domains.records.type'),
            value: (
              <Text size="small" className="font-mono">
                {type}
              </Text>
            ),
          },
          {
            label: nameLabel,
            value: <CopyableValue value={name} label={nameLabel} />,
          },
          {
            label: valueLabel,
            value: <CopyableValue value={value} label={valueLabel} />,
          },
        ]}
      />
    </div>
  );
};
